import React, { useState, useRef, useEffect } from 'react';
import { Upload, AlertCircle, CheckCircle, Download, X, FileText } from 'lucide-react';
import api from '../utils/api';
import { normalizeText, decodeCsvBuffer, detectDelimiter, parseCsvLine, parseDate, parseValor } from '../utils/csvUtils';

// Modelo no padrão do Excel em português: ponto e vírgula, data DD/MM/AAAA e valor com vírgula.
const CSV_TEMPLATE = [
  'data;descricao;tipo;valor;categoria;status',
  '15/01/2026;Aluguel;DESPESA;1500,00;Aluguel;pago',
  '20/01/2026;Salário;RECEITA;5000,00;Outras Receitas;pago',
  '01/02/2026;Internet;DESPESA;150,00;Despesas Fixas;pendente',
].join('\r\n');

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [], error: 'Arquivo vazio ou sem dados.' };

  const delimiter = detectDelimiter(lines[0]);
  const headers = parseCsvLine(lines[0], delimiter).map(h => normalizeText(h).replace(/["']/g, ''));
  const required = ['data', 'descricao', 'tipo', 'valor'];
  const missing = required.filter(r => !headers.includes(r));
  if (missing.length) return { headers, rows: [], error: `Colunas obrigatórias faltando: ${missing.join(', ')}` };

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i], delimiter);
    const row = {};
    headers.forEach((h, idx) => { row[h] = (cols[idx] || '').replace(/^"|"$/g, '').trim(); });
    row._data = parseDate(row.data);
    row._valor = parseValor(row.valor);
    rows.push({ ...row, _line: i + 1 });
  }
  return { headers, rows, error: null };
}

function validateRow(row) {
  const errors = [];
  if (!row._data) errors.push('data inválida (use DD/MM/AAAA)');
  if (!row.descricao) errors.push('descrição obrigatória');
  if (!['RECEITA', 'DESPESA', 'CUSTO'].includes((row.tipo || '').toUpperCase())) errors.push('tipo deve ser RECEITA, DESPESA ou CUSTO');
  if (isNaN(row._valor) || row._valor <= 0) errors.push('valor inválido');
  return errors;
}

export default function ImportacaoCSVModal({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState('upload'); // upload | preview | result
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [reading, setReading] = useState(false);
  const [carteiras, setCarteiras] = useState([]);
  const [carteiraId, setCarteiraId] = useState('');
  const inputRef = useRef();

  useEffect(() => {
    if (open) api.get('/carteiras').then(r => setCarteiras(r.data || [])).catch(() => {});
  }, [open]);

  if (!open) return null;

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setStep('upload');
    setResult(null);
    setError('');
    setCarteiraId('');
  };

  // Extrato em PDF: o servidor lê as colunas e devolve os movimentos para revisão.
  const handlePdf = async (f) => {
    setFile(f);
    setError('');
    setReading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await api.post('/lancamentos/importar-pdf/previa', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const rows = res.data.movimentos.map((m, i) => {
        const [ano, mes, dia] = m.data.split('-');
        const valor = Math.abs(m.valor);
        const row = {
          data: `${dia}/${mes}/${ano}`,
          descricao: m.descricao,
          tipo: m.tipo,
          valor: String(valor),
          _data: m.data,
          _valor: valor,
          _line: i + 1,
          _pdf: true,
          _idExterno: m.idExterno,
          _interno: m.interno,
          _duplicado: m.duplicado,
          _ignorar: m.interno || m.duplicado,
          status: m.tipo === 'RECEITA' ? 'recebido' : 'pago',
        };
        return { ...row, _errors: validateRow(row) };
      });
      setPreview({ headers: [], rows, pdf: true, conferido: res.data.conferido });
      setStep('preview');
    } catch (err) {
      setError(err.response?.data?.detail || 'Não foi possível ler o PDF.');
    } finally {
      setReading(false);
    }
  };

  const toggleIgnorar = (idx) => {
    setPreview(p => ({ ...p, rows: p.rows.map((r, i) => (i === idx ? { ...r, _ignorar: !r._ignorar } : r)) }));
  };

  const handleFile = (f) => {
    if (!f) return;
    const nome = f.name.toLowerCase();
    if (nome.endsWith('.pdf')) { handlePdf(f); return; }
    if (!nome.endsWith('.csv')) { setError('Apenas arquivos .csv ou .pdf são aceitos.'); return; }
    setFile(f);
    setError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = decodeCsvBuffer(e.target.result);
      const parsed = parseCsv(text);
      if (parsed.error) { setError(parsed.error); return; }
      const rowsWithValidation = parsed.rows.map(row => ({
        ...row,
        _errors: validateRow(row),
      }));
      setPreview({ ...parsed, rows: rowsWithValidation });
      setStep('preview');
    };
    reader.readAsArrayBuffer(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const validRows = preview?.rows.filter(r => r._errors.length === 0 && !r._ignorar) || [];
  const ignoredCount = preview?.rows.filter(r => r._errors.length === 0 && r._ignorar).length || 0;
  const invalidRows = preview?.rows.filter(r => r._errors.length > 0) || [];

  const handleImport = async () => {
    if (validRows.length === 0) { setError('Nenhuma linha válida para importar.'); return; }
    setImporting(true);
    setError('');
    try {
      const carteira = carteiras.find(c => c.id === carteiraId);
      const lancamentos = validRows.map(row => ({
        ...(row._pdf ? {
          idExterno: row._idExterno,
          origemImportacao: 'PDF',
          carteiraId: carteira?.id || '',
          carteiraNome: carteira?.nome || '',
          carteiraCor: carteira?.cor || '',
        } : {}),
        dataCompetencia: row._data,
        descricao: row.descricao,
        tipo: (row.tipo || '').toUpperCase(),
        valor: row._valor,
        categoria: row.categoria || null,
        status: ['pago', 'recebido', 'pendente', 'atrasado'].includes(normalizeText(row.status)) ? normalizeText(row.status) : 'pendente',
      }));
      const res = await api.post('/lancamentos/importar', { lancamentos });
      setResult(res.data);
      setStep('result');
      onImported?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao importar.');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    // BOM UTF-8: o Excel reconhece os acentos (Salário, Serviços...)
    const blob = new Blob(['\uFEFF' + CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modelo_importacao.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 nexus-modal-overlay">
      <div className="nexus-modal w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText size={18} className="text-slate-500" />
            <h2 className="font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Importar CSV ou PDF
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 mb-4">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-4">
              <div
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => inputRef.current?.click()}
                className="border-2 border-dashed border-slate-200 dark:border-slate-600 rounded-2xl p-10 text-center cursor-pointer hover:border-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors"
              >
                <Upload size={32} className="mx-auto mb-3 text-slate-300" />
                <p className="font-semibold text-slate-600 dark:text-slate-300">{reading ? 'Lendo o PDF...' : 'Clique ou arraste o arquivo CSV ou PDF'}</p>
                <p className="text-sm text-slate-400 mt-1">Planilha .csv ou extrato bancário .pdf (ex.: Mercado Pago)</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,.pdf"
                  className="hidden"
                  onChange={e => handleFile(e.target.files[0])}
                />
              </div>

              <div className="bg-slate-50 dark:bg-slate-700/50 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Formato esperado</p>
                  <button
                    onClick={downloadTemplate}
                    className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    <Download size={12} /> Baixar modelo
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-600">
                        {['data', 'descricao', 'tipo', 'valor', 'categoria*', 'status*'].map(h => (
                          <th key={h} className="text-left pb-2 pr-4 font-bold text-slate-500">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="text-slate-400">
                        <td className="pt-2 pr-4">DD/MM/AAAA</td>
                        <td className="pt-2 pr-4">texto</td>
                        <td className="pt-2 pr-4">RECEITA/DESPESA/CUSTO</td>
                        <td className="pt-2 pr-4">1500,00</td>
                        <td className="pt-2 pr-4">opcional</td>
                        <td className="pt-2 pr-4">pago/pendente</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-slate-400 mt-2">* campos opcionais</p>
                <p className="text-xs text-slate-400 mt-1">
                  Separe as colunas com ponto e vírgula (;), como o Excel em português salva. Aceitamos também vírgula.
                  Data em DD/MM/AAAA e valor com vírgula (1500,00).
                </p>
              </div>
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{preview.rows.length}</p>
                  <p className="text-xs text-slate-400 font-medium">Total de linhas</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-emerald-600">{validRows.length}</p>
                  <p className="text-xs text-emerald-600 font-medium">Válidas</p>
                </div>
                <div className={`rounded-xl p-3 text-center ${invalidRows.length > 0 ? 'bg-red-50 dark:bg-red-900/20' : 'bg-slate-50 dark:bg-slate-700/50'}`}>
                  <p className={`text-xl font-bold ${invalidRows.length > 0 ? 'text-red-600' : 'text-slate-400'}`}>{invalidRows.length}</p>
                  <p className={`text-xs font-medium ${invalidRows.length > 0 ? 'text-red-500' : 'text-slate-400'}`}>Com erros</p>
                </div>
              </div>

              {preview.pdf && (
                <div className="space-y-3">
                  <div className={`rounded-xl p-3 text-xs ${preview.conferido === false ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>
                    {preview.conferido === false
                      ? 'Atenção: a soma dos movimentos não bate com o saldo do extrato. Revise a prévia antes de importar.'
                      : preview.conferido
                        ? 'Conferido: saldo inicial + movimentos = saldo final do extrato.'
                        : 'Não foi possível conferir os saldos do extrato.'}
                    {ignoredCount > 0 && ` ${ignoredCount} movimento${ignoredCount !== 1 ? 's' : ''} desmarcado${ignoredCount !== 1 ? 's' : ''} (reservas/retiradas, pagamento de fatura do cartão — já contado nas compras da fatura — ou já importados).`}
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500">Carteira dos lançamentos</label>
                    <select value={carteiraId} onChange={e => setCarteiraId(e.target.value)} className="fin-input w-full mt-1">
                      <option value="">Sem carteira</option>
                      {carteiras.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {/* Preview table */}
              <div className="border border-slate-100 dark:border-slate-700 rounded-xl overflow-hidden">
                <div className="bg-slate-50 dark:bg-slate-700/50 px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Prévia dos dados</p>
                </div>
                <div className="overflow-x-auto max-h-64">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white dark:bg-slate-800">
                      <tr className="border-b border-slate-100 dark:border-slate-700">
                        {preview.pdf && <th className="px-4 py-2" />}
                        <th className="text-left px-4 py-2 text-slate-400 font-bold">#</th>
                        <th className="text-left px-4 py-2 text-slate-400 font-bold">Data</th>
                        <th className="text-left px-4 py-2 text-slate-400 font-bold">Descrição</th>
                        <th className="text-left px-4 py-2 text-slate-400 font-bold">Tipo</th>
                        <th className="text-left px-4 py-2 text-slate-400 font-bold">Valor</th>
                        <th className="text-left px-4 py-2 text-slate-400 font-bold">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 dark:divide-slate-700">
                      {preview.rows.slice(0, 50).map((row, idx) => (
                        <tr key={idx} className={row._errors.length > 0 ? 'bg-red-50/50 dark:bg-red-900/10' : ''}>
                          {preview.pdf && (
                            <td className="px-4 py-2">
                              <input type="checkbox" checked={!row._ignorar} onChange={() => toggleIgnorar(idx)} title={row._duplicado ? 'Já importado antes' : row._interno ? 'Reserva/retirada ou pagamento de fatura (já contado no cartão)' : 'Importar este movimento'} />
                            </td>
                          )}
                          <td className="px-4 py-2 text-slate-400">{row._line}</td>
                          <td className="px-4 py-2 text-slate-600 dark:text-slate-300">{row.data}</td>
                          <td className="px-4 py-2 text-slate-700 dark:text-slate-200 max-w-[150px] truncate">{row.descricao}</td>
                          <td className="px-4 py-2">
                            <span className={`px-1.5 py-0.5 rounded font-bold text-white`}
                              style={{
                                background: row.tipo === 'RECEITA' ? '#339933' : row.tipo === 'DESPESA' ? '#d72d21' : row.tipo === 'CUSTO' ? '#c99a1a' : '#94a3b8',
                                fontSize: '10px'
                              }}>
                              {row.tipo || '?'}
                            </span>
                          </td>
                          <td className="px-4 py-2 font-bold text-slate-700 dark:text-slate-200">
                            {!isNaN(row._valor) ? `R$ ${row._valor.toFixed(2).replace('.', ',')}` : '?'}
                          </td>
                          <td className="px-4 py-2">
                            {row._errors.length > 0 ? (
                              <span className="text-red-500 font-semibold flex items-center gap-1" title={row._errors.join(', ')}>
                                <AlertCircle size={11} /> Erro
                              </span>
                            ) : (
                              <span className="text-emerald-600 font-semibold">✓</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.rows.length > 50 && (
                    <p className="text-xs text-slate-400 text-center py-2">
                      Mostrando 50 de {preview.rows.length} linhas
                    </p>
                  )}
                </div>
              </div>

              {/* Errors detail */}
              {invalidRows.length > 0 && (
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl p-4">
                  <p className="text-xs font-bold text-red-600 mb-2 uppercase tracking-wider">Linhas com erros (serão ignoradas)</p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {invalidRows.map((row, idx) => (
                      <p key={idx} className="text-xs text-red-600">
                        Linha {row._line}: {row._errors.join('; ')}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 'result' && result && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-1">Importação concluída!</h3>
              <p className="text-slate-500 text-sm">
                <strong className="text-emerald-600">{result.criados}</strong> lançamento{result.criados !== 1 ? 's' : ''} importado{result.criados !== 1 ? 's' : ''} com sucesso.
              </p>
              {result.erros?.length > 0 && (
                <p className="text-sm text-red-500 mt-1">{result.erros.length} registro{result.erros.length !== 1 ? 's' : ''} ignorado{result.erros.length !== 1 ? 's' : ''} por erros.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 flex justify-between gap-3 flex-shrink-0">
          {step === 'upload' && (
            <button onClick={onClose} className="fin-btn-secondary ml-auto">Cancelar</button>
          )}
          {step === 'preview' && (
            <>
              <button onClick={handleReset} className="fin-btn-secondary">← Voltar</button>
              <button
                onClick={handleImport}
                disabled={importing || validRows.length === 0}
                className="fin-btn-primary"
              >
                {importing ? 'Importando...' : `Importar ${validRows.length} lançamento${validRows.length !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
          {step === 'result' && (
            <>
              <button onClick={handleReset} className="fin-btn-secondary">Importar outro</button>
              <button onClick={onClose} className="fin-btn-primary">Fechar</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
